import { useState, useRef, useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import SearchIcon from '@mui/icons-material/Search'
import MenuIcon from '@mui/icons-material/Menu'
import NotificationsIcon from '@mui/icons-material/Notifications'
import UserMenu from './UserMenu'
import PendingChangesIndicator from './PendingChangesIndicator'
import TasksIndicator from './TasksIndicator'
import SearchResults from './SearchResults'

const SIDEBAR_WIDTH = 240

function TopBar({ showTasksIndicator = false, showPendingChanges = false, onMenuToggle }) {
  const intl = useIntl()
  const location = useLocation()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [searchValue, setSearchValue] = useState('')
  const [showResults, setShowResults] = useState(false)
  const searchRef = useRef(null)

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value)
    setShowResults(true)
  }

  const handleClickAway = useCallback(() => {
    setShowResults(false)
  }, [])

  const handleFocus = () => {
    if (searchValue.trim().length >= 2) {
      setShowResults(true)
    }
  }

  const handleClose = useCallback(() => {
    setSearchValue('')
    setShowResults(false)
  }, [])

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClose()
      searchRef.current?.querySelector('input')?.blur()
    }
  }

  useEffect(() => {
    handleClose()
  }, [location.pathname, handleClose])

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: 'navigation.main',
        color: '#fff',
      }}
    >
      <Toolbar>
        {!isDesktop && (
          <IconButton
            edge="start"
            sx={{ color: '#fff', mr: 1 }}
            onClick={onMenuToggle}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box
          sx={{
            width: { xs: 'auto', md: SIDEBAR_WIDTH - 24 },
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600, color: '#fff' }}>
            Okonomi
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', px: 2 }}>
          <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{ width: '100%', maxWidth: 500, position: 'relative' }}>
              <TextField
                ref={searchRef}
                size="small"
                fullWidth
                placeholder={intl.formatMessage({ id: 'navigation.search.placeholder' })}
                value={searchValue}
                onChange={handleSearchChange}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255,255,255,0.5)',
                    opacity: 1,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <SearchResults
                searchValue={searchValue}
                anchorEl={searchRef.current}
                open={showResults}
                onClose={handleClose}
              />
            </Box>
          </ClickAwayListener>
        </Box>
        {isDesktop || <Box sx={{ flexGrow: 1 }} />}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {showPendingChanges && <PendingChangesIndicator />}
          {showTasksIndicator && <TasksIndicator />}
          <IconButton sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
            <NotificationsIcon />
          </IconButton>
          <UserMenu />
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default TopBar
